/**
 * Dynamic Header Component JavaScript
 * dynamic-header.js
 * Handles ambient video backgrounds and featured video modals
 * Uses WordPress-compatible utility patterns
 */

(function (global) {
  "use strict";

  /**
   * Dynamic Header Video Manager Class
   */
  function DynamicHeaderManager() {
    this.activeModal = null;
    this.youtubeAPIReady = false;
    this.observers = [];
    this.stateMachine = null;
    this.config = {
      selectors: {
        header: ".page-header",
        ambientVideo: ".page-header-video-bg-ambient",
        featuredButton: ".video-play-btn",
        videoContainer: ".hero__video-bg",
        verticalVideo: ".hero__video-vertical",
        fallbackButton: ".video-fallback-play",
      },
      classes: {
        active: "is-active",
        loading: "is-loading",
        error: "has-error",
      },
    };

    // Initialize state machine for video states
    if (global.createStateMachine) {
      this.stateMachine = global.createStateMachine({
        initialState: "idle",
        states: {
          idle: {
            enter: () => console.log("Video manager idle"),
          },
          loading: {
            enter: () => {
              console.log("Loading videos...");
              document.body.classList.add(this.config.classes.loading);
            },
            exit: () => {
              document.body.classList.remove(this.config.classes.loading);
            },
          },
          ready: {
            enter: () => console.log("Videos ready"),
          },
          error: {
            enter: (data) => {
              console.error("Video error:", data);
              document.body.classList.add(this.config.classes.error);
            },
            exit: () => {
              document.body.classList.remove(this.config.classes.error);
            },
          },
        },
        transitions: {
          idle: ["loading"],
          loading: ["ready", "error"],
          ready: ["loading"],
          error: ["loading", "idle"],
        },
      });
    }

    this.init();
  }

  DynamicHeaderManager.prototype.init = function () {
    if (this.stateMachine) {
      this.stateMachine.transition("loading");
    }

    // Initialize ambient video backgrounds
    this.initAmbientVideos();

    // Initialize featured video buttons
    this.initFeaturedVideos();

    // Handle responsive video scaling
    this.handleResponsiveVideos();

    // Setup intersection observer for performance
    this.setupIntersectionObserver();

    // Initialize with accessibility considerations
    this.initWithAccessibility();

    if (this.stateMachine) {
      this.stateMachine.transition("ready");
    }
  };

  /**
   * Initialize ambient video backgrounds
   */
  DynamicHeaderManager.prototype.initAmbientVideos = function () {
    const ambientHeaders = document.querySelectorAll(
      this.config.selectors.ambientVideo
    );

    ambientHeaders.forEach((header) => {
      const videoProvider = header.dataset.videoProvider;
      const videoUrl = header.dataset.videoUrl;
      const mediaOrientation = header.dataset.mediaOrientation || "landscape";

      if (!videoUrl) return;

      switch (videoProvider) {
        case "youtube":
          this.loadYouTubeAmbient(header, videoUrl, mediaOrientation);
          break;
        case "vimeo":
          this.loadVimeoAmbient(header, videoUrl, mediaOrientation);
          break;
        default:
          this.loadHTML5Ambient(header, videoUrl, mediaOrientation);
          break;
      }
    });
  };

  /**
   * Load YouTube ambient video
   */
  DynamicHeaderManager.prototype.loadYouTubeAmbient = function (
    header,
    videoUrl,
    orientation
  ) {
    const videoId = this.extractYouTubeId(videoUrl);
    if (!videoId) return;

    const containerSelector =
      orientation === "vertical"
        ? ".video-embed--youtube"
        : ".video-embed--youtube";
    const embedContainer = header.querySelector(containerSelector);
    if (!embedContainer) return;

    // Load YouTube API if not already loaded
    if (!global.YT) {
      this.loadYouTubeAPI(() => {
        this.createYouTubePlayer(embedContainer, videoId);
      });
    } else if (global.YT.loaded) {
      this.createYouTubePlayer(embedContainer, videoId);
    } else {
      // API is loading, wait for it
      const checkReady = setInterval(() => {
        if (global.YT && global.YT.loaded) {
          clearInterval(checkReady);
          this.createYouTubePlayer(embedContainer, videoId);
        }
      }, 100);
    }
  };

  /**
   * Create YouTube player
   */
  DynamicHeaderManager.prototype.createYouTubePlayer = function (
    container,
    videoId
  ) {
    const playerId =
      "youtube-ambient-" + Date.now() + Math.random().toString(36).substr(2, 9);
    container.id = playerId;

    try {
      const player = new global.YT.Player(playerId, {
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          loop: 1,
          controls: 0,
          showinfo: 0,
          rel: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playlist: videoId,
          start: 0,
          fs: 0,
          cc_load_policy: 0,
          disablekb: 1,
          enablejsapi: 1,
        },
        events: {
          onReady: (event) => {
            event.target.mute();
            event.target.setVolume(0);
            event.target.playVideo();
          },
          onStateChange: (event) => {
            // Loop the video when it ends
            if (event.data === global.YT.PlayerState.ENDED) {
              event.target.seekTo(0);
              event.target.playVideo();
            }
          },
          onError: (event) => {
            console.error("YouTube player error:", event.data);
            if (this.stateMachine) {
              this.stateMachine.transition("error", {
                type: "youtube",
                code: event.data,
              });
            }
            this.showVideoFallback(
              container.closest(this.config.selectors.ambientVideo)
            );
          },
        },
      });
    } catch (error) {
      console.error("Error creating YouTube player:", error);
      if (this.stateMachine) {
        this.stateMachine.transition("error", {
          type: "youtube_create",
          error,
        });
      }
      this.showVideoFallback(
        container.closest(this.config.selectors.ambientVideo)
      );
    }
  };

  /**
   * Load Vimeo ambient video
   */
  DynamicHeaderManager.prototype.loadVimeoAmbient = function (
    header,
    videoUrl,
    orientation
  ) {
    const videoId = this.extractVimeoId(videoUrl);
    if (!videoId) return;

    const containerSelector =
      orientation === "vertical"
        ? ".video-embed--vimeo"
        : ".video-embed--vimeo";
    const embedContainer = header.querySelector(containerSelector);
    if (!embedContainer) return;

    const iframe = document.createElement("iframe");
    iframe.src =
      "https://player.vimeo.com/video/" +
      videoId +
      "?autoplay=1&muted=1&loop=1&background=1&controls=0&byline=0&portrait=0&title=0";
    iframe.frameBorder = "0";
    iframe.allow = "autoplay; fullscreen";
    iframe.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;";
    iframe.setAttribute("aria-hidden", "true");

    const self = this;
    iframe.onerror = function () {
      console.error("Vimeo iframe failed to load");
      if (self.stateMachine) {
        self.stateMachine.transition("error", { type: "vimeo" });
      }
      self.showVideoFallback(header);
    };

    embedContainer.appendChild(iframe);
  };

  /**
   * Load HTML5 ambient video
   */
  DynamicHeaderManager.prototype.loadHTML5Ambient = function (
    header,
    videoUrl,
    orientation
  ) {
    const videoSelector =
      orientation === "vertical"
        ? ".hero__video-vertical-video"
        : ".hero__video";
    const video = header.querySelector(videoSelector);
    if (!video) return;

    const self = this;

    if (global.on) {
      // Use utility function for event handling
      global.on(video, "loadedmetadata", function () {
        video.play().catch((e) => {
          console.log("HTML5 video autoplay prevented:", e);
          self.showVideoFallback(header);
        });
      });

      global.on(video, "error", function (e) {
        console.error("HTML5 video error:", e);
        if (self.stateMachine) {
          self.stateMachine.transition("error", { type: "html5", error: e });
        }
        self.showVideoFallback(header);
      });
    } else {
      // Fallback to standard event listeners
      video.addEventListener("loadedmetadata", function () {
        video.play().catch((e) => {
          console.log("HTML5 video autoplay prevented:", e);
          self.showVideoFallback(header);
        });
      });

      video.addEventListener("error", function (e) {
        console.error("HTML5 video error:", e);
        self.showVideoFallback(header);
      });
    }

    // Set video source if not already set
    if (!video.querySelector("source") && !video.src) {
      video.src = videoUrl;
    }
  };

  /**
   * Initialize featured video buttons
   */
  DynamicHeaderManager.prototype.initFeaturedVideos = function () {
    const playButtons = document.querySelectorAll(
      this.config.selectors.featuredButton
    );
    const self = this;

    if (global.on) {
      // Use utility function for event handling
      global.on(playButtons, "click", function (e) {
        e.preventDefault();

        const button = e.currentTarget;
        const videoProvider = button.dataset.videoProvider;
        const videoUrl = button.dataset.videoUrl;
        const runtime = button.dataset.videoRuntime;

        self.openVideoModal(videoProvider, videoUrl, runtime);
      });

      // Add keyboard support
      global.on(playButtons, "keydown", function (e) {
        if (
          global.KEYCODES &&
          (e.key === global.KEYCODES.ENTER || e.key === global.KEYCODES.SPACE)
        ) {
          e.preventDefault();
          e.currentTarget.click();
        }
      });
    } else {
      // Fallback to standard event listeners
      playButtons.forEach(function (button) {
        button.addEventListener("click", function (e) {
          e.preventDefault();

          const videoProvider = button.dataset.videoProvider;
          const videoUrl = button.dataset.videoUrl;
          const runtime = button.dataset.videoRuntime;

          self.openVideoModal(videoProvider, videoUrl, runtime);
        });

        button.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            button.click();
          }
        });
      });
    }
  };

  /**
   * Open video in fullscreen modal
   */
  DynamicHeaderManager.prototype.openVideoModal = function (
    provider,
    url,
    runtime
  ) {
    runtime = runtime || null;

    // Close existing modal if open
    if (this.activeModal) {
      this.closeVideoModal(this.activeModal);
    }

    const modal = this.createVideoModal();
    const modalContent = modal.querySelector(".video-modal-content");

    let embedHtml = "";

    switch (provider) {
      case "youtube":
        const youtubeId = this.extractYouTubeId(url);
        if (youtubeId) {
          embedHtml =
            '<iframe src="https://www.youtube.com/embed/' +
            youtubeId +
            '?autoplay=1&rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&enablejsapi=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowfullscreen title="Video Player"></iframe>';
        }
        break;

      case "vimeo":
        const vimeoId = this.extractVimeoId(url);
        if (vimeoId) {
          embedHtml =
            '<iframe src="https://player.vimeo.com/video/' +
            vimeoId +
            '?autoplay=1&color=ffffff&title=0&byline=0&portrait=0&badge=0" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="Video Player"></iframe>';
        }
        break;

      default:
        if (url) {
          embedHtml =
            '<video controls autoplay controlslist="nodownload"><source src="' +
            url +
            '" type="video/mp4"><p>Your browser does not support the video tag.</p></video>';
        }
        break;
    }

    if (embedHtml) {
      modalContent.innerHTML = embedHtml;
      document.body.appendChild(modal);
      this.activeModal = modal;

      // Prevent body scroll
      document.body.style.overflow = "hidden";

      // Animate in
      requestAnimationFrame(() => {
        modal.classList.add(this.config.classes.active);
      });

      // Focus management
      const closeButton = modal.querySelector(".video-modal-close");
      if (closeButton) {
        closeButton.focus();
      }
    }
  };

  /**
   * Create video modal HTML
   */
  DynamicHeaderManager.prototype.createVideoModal = function () {
    const modal = document.createElement("div");
    modal.className = "video-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", "Video Player");

    modal.innerHTML =
      '<div class="video-modal-overlay" aria-hidden="true"></div><div class="video-modal-container"><button class="video-modal-close" aria-label="Close video" title="Close video (Escape)"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></button><div class="video-modal-content" role="main"></div></div>';

    // Add modal styles if not already added
    this.injectModalStyles();

    // Add event listeners
    const closeButton = modal.querySelector(".video-modal-close");
    const overlay = modal.querySelector(".video-modal-overlay");
    const self = this;

    if (global.on) {
      if (closeButton) {
        global.on(closeButton, "click", function () {
          self.closeVideoModal(modal);
        });
      }

      if (overlay) {
        global.on(overlay, "click", function () {
          self.closeVideoModal(modal);
        });
      }

      // ESC key support
      global.on(document, "keydown", function (e) {
        if (
          e.key === (global.KEYCODES ? global.KEYCODES.ESCAPE : "Escape") &&
          modal.classList.contains(self.config.classes.active)
        ) {
          e.preventDefault();
          self.closeVideoModal(modal);
        }
        // Trap focus within modal
        else if (
          e.key === (global.KEYCODES ? global.KEYCODES.TAB : "Tab") &&
          modal.classList.contains(self.config.classes.active)
        ) {
          self.trapFocus(modal, e);
        }
      });
    } else {
      // Fallback event listeners
      if (closeButton) {
        closeButton.addEventListener("click", function () {
          self.closeVideoModal(modal);
        });
      }

      if (overlay) {
        overlay.addEventListener("click", function () {
          self.closeVideoModal(modal);
        });
      }

      document.addEventListener("keydown", function (e) {
        if (
          e.key === "Escape" &&
          modal.classList.contains(self.config.classes.active)
        ) {
          e.preventDefault();
          self.closeVideoModal(modal);
        } else if (
          e.key === "Tab" &&
          modal.classList.contains(self.config.classes.active)
        ) {
          self.trapFocus(modal, e);
        }
      });
    }

    return modal;
  };

  /**
   * Trap focus within modal
   */
  DynamicHeaderManager.prototype.trapFocus = function (modal, e) {
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  /**
   * Close video modal
   */
  DynamicHeaderManager.prototype.closeVideoModal = function (modal) {
    if (!modal) return;

    modal.classList.remove(this.config.classes.active);

    // Restore body scroll
    document.body.style.overflow = "";

    const self = this;
    setTimeout(
      function () {
        if (modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
      },
      global.ANIMATION_DURATION ? global.ANIMATION_DURATION.normal : 300
    );

    this.activeModal = null;
  };

  /**
   * Inject modal styles if not already present
   */
  DynamicHeaderManager.prototype.injectModalStyles = function () {
    if (document.querySelector("#video-modal-styles")) return;

    const styles = document.createElement("style");
    styles.id = "video-modal-styles";
    styles.textContent =
      ".video-modal{position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center;z-index:9999;opacity:0;visibility:hidden;transition:all 0.3s ease}.video-modal.is-active{opacity:1;visibility:visible}.video-modal-container{position:relative;width:90vw;max-width:1200px;aspect-ratio:16/9;background:#000;border-radius:8px;overflow:hidden;transform:scale(0.8);transition:transform 0.3s ease}.video-modal.is-active .video-modal-container{transform:scale(1)}.video-modal-content{width:100%;height:100%}.video-modal-content iframe,.video-modal-content video{width:100%;height:100%;border:none}.video-modal-close{position:absolute;top:-50px;right:0;background:rgba(0,0,0,0.5);border:none;color:white;font-size:24px;cursor:pointer;padding:10px;border-radius:50%;width:44px;height:44px;display:flex;align-items:center;justify-content:center;transition:background 0.2s ease;backdrop-filter:blur(10px)}.video-modal-close:hover,.video-modal-close:focus{background:rgba(255,255,255,0.2);outline:2px solid rgba(255,255,255,0.5);outline-offset:2px}@media (max-width:768px){.video-modal-container{width:95vw}.video-modal-close{top:-40px;right:-10px;font-size:20px;width:36px;height:36px}}";
    document.head.appendChild(styles);
  };

  /**
   * Handle responsive video scaling
   */
  DynamicHeaderManager.prototype.handleResponsiveVideos = function () {
    const self = this;

    const resizeHandler = function () {
      const videos = document.querySelectorAll(".hero__video, .video-embed");

      videos.forEach(function (video) {
        const container = video.closest(".hero__video-bg");
        if (!container) return;

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        const videoAspectRatio = 16 / 9;
        const containerAspectRatio = containerWidth / containerHeight;

        if (containerAspectRatio > videoAspectRatio) {
          // Container is wider than video aspect ratio
          video.style.width = "100%";
          video.style.height = "auto";
          video.style.minHeight = "100%";
        } else {
          // Container is taller than video aspect ratio
          video.style.width = "auto";
          video.style.height = "100%";
          video.style.minWidth = "100%";
        }
      });
    };

    // Debounce resize handler
    let resizeTimeout;
    const debouncedResize = function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeHandler, 150);
    };

    if (global.on) {
      global.on(window, "resize", debouncedResize);
      global.on(window, "orientationchange", function () {
        setTimeout(resizeHandler, 500); // Delay for orientation change
      });
    } else {
      window.addEventListener("resize", debouncedResize);
      window.addEventListener("orientationchange", function () {
        setTimeout(resizeHandler, 500);
      });
    }

    // Initial call
    setTimeout(resizeHandler, 100);
  };

  /**
   * Setup intersection observer for performance optimization
   */
  DynamicHeaderManager.prototype.setupIntersectionObserver = function () {
    if (!("IntersectionObserver" in window)) return;

    const self = this;
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const video = entry.target.querySelector("video");

          if (entry.isIntersecting) {
            // Video is visible, ensure it's playing
            if (video && video.paused && !self.prefersReducedMotion()) {
              video.play().catch(function (e) {
                console.log("Autoplay prevented:", e);
              });
            }
          } else {
            // Video is not visible, pause to save resources
            if (video && !video.paused) {
              video.pause();
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    // Observe all ambient video headers
    const ambientHeaders = document.querySelectorAll(
      this.config.selectors.ambientVideo
    );
    ambientHeaders.forEach(function (header) {
      observer.observe(header);
      self.observers.push(observer);
    });
  };

  /**
   * Extract YouTube video ID from various URL formats
   */
  DynamicHeaderManager.prototype.extractYouTubeId = function (url) {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  /**
   * Extract Vimeo video ID from URL
   */
  DynamicHeaderManager.prototype.extractVimeoId = function (url) {
    const regExp =
      /(?:vimeo)\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const match = url.match(regExp);
    return match ? match[3] : null;
  };

  /**
   * Load YouTube API
   */
  DynamicHeaderManager.prototype.loadYouTubeAPI = function (callback) {
    if (global.YT && global.YT.Player) {
      callback();
      return;
    }

    // Set up the callback
    global.onYouTubeIframeAPIReady = function () {
      this.youtubeAPIReady = true;
      callback();
    }.bind(this);

    // Load the script if not already loading
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = function () {
        console.error("Failed to load YouTube API");
      };
      document.head.appendChild(script);
    }
  };

  /**
   * Show fallback when video fails to autoplay
   */
  DynamicHeaderManager.prototype.showVideoFallback = function (header) {
    if (!header || header.querySelector(".video-fallback")) return;

    const fallback = document.createElement("div");
    fallback.className = "video-fallback";
    fallback.innerHTML =
      '<div class="video-fallback-content"><button class="video-fallback-play" aria-label="Play background video"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg><span>Click to play video</span></button></div>';

    // Inject fallback styles if not present
    this.injectFallbackStyles();

    const playButton = fallback.querySelector(".video-fallback-play");
    const self = this;

    if (global.on) {
      global.on(playButton, "click", function () {
        const video = header.querySelector("video");
        const iframe = header.querySelector("iframe");

        if (video) {
          video
            .play()
            .then(function () {
              fallback.remove();
            })
            .catch(function (e) {
              console.log("Video play failed:", e);
            });
        } else if (iframe) {
          // For embedded videos, just hide the fallback
          fallback.remove();
        }
      });
    } else {
      playButton.addEventListener("click", function () {
        const video = header.querySelector("video");
        const iframe = header.querySelector("iframe");

        if (video) {
          video
            .play()
            .then(function () {
              fallback.remove();
            })
            .catch(function (e) {
              console.log("Video play failed:", e);
            });
        } else if (iframe) {
          fallback.remove();
        }
      });
    }

    const videoBg =
      header.querySelector(".hero__video-bg") ||
      header.querySelector(".hero__video-vertical");
    if (videoBg) {
      videoBg.appendChild(fallback);
    }
  };

  /**
   * Inject fallback styles
   */
  DynamicHeaderManager.prototype.injectFallbackStyles = function () {
    if (document.querySelector("#video-fallback-styles")) return;

    const styles = document.createElement("style");
    styles.id = "video-fallback-styles";
    styles.textContent =
      ".video-fallback{position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:5}.video-fallback-play{display:flex;align-items:center;gap:0.5rem;padding:1rem 2rem;background:rgba(255,255,255,0.1);color:white;border:2px solid rgba(255,255,255,0.3);border-radius:50px;font-size:1rem;cursor:pointer;transition:all 0.3s ease;backdrop-filter:blur(10px)}.video-fallback-play:hover,.video-fallback-play:focus{background:rgba(255,255,255,0.2);border-color:rgba(255,255,255,0.5);transform:translateY(-2px);outline:2px solid rgba(255,255,255,0.3);outline-offset:2px}.video-fallback-play svg{width:20px;height:20px;flex-shrink:0}";
    document.head.appendChild(styles);
  };

  /**
   * Utility method to check if device prefers reduced motion
   */
  DynamicHeaderManager.prototype.prefersReducedMotion = function () {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  };

  /**
   * Initialize with reduced motion considerations
   */
  DynamicHeaderManager.prototype.initWithAccessibility = function () {
    if (this.prefersReducedMotion()) {
      // Disable autoplay for users who prefer reduced motion
      const videos = document.querySelectorAll("video[autoplay]");
      videos.forEach(function (video) {
        video.removeAttribute("autoplay");
        video.setAttribute("controls", "");
        video.pause();
      });

      // Show message about reduced motion
      const ambientHeaders = document.querySelectorAll(
        this.config.selectors.ambientVideo
      );
      ambientHeaders.forEach(function (header) {
        const message = document.createElement("div");
        message.className = "reduced-motion-notice";
        message.innerHTML =
          "<p>Video autoplay disabled due to reduced motion preference</p><button onclick=\"this.parentElement.style.display='none'; this.closest('.page-header').querySelector('video')?.play()\">Play Video</button>";
        header.appendChild(message);
      });
    }
  };

  /**
   * Cleanup method for destroying instance
   */
  DynamicHeaderManager.prototype.destroy = function () {
    // Close any open modals
    if (this.activeModal) {
      this.closeVideoModal(this.activeModal);
    }

    // Cleanup observers
    this.observers.forEach(function (observer) {
      if (observer && observer.disconnect) {
        observer.disconnect();
      }
    });

    // Remove injected styles
    const modalStyles = document.querySelector("#video-modal-styles");
    const fallbackStyles = document.querySelector("#video-fallback-styles");

    if (modalStyles) modalStyles.remove();
    if (fallbackStyles) fallbackStyles.remove();

    this.observers = [];
  };

  // Make the manager available globally
  global.DynamicHeaderManager = DynamicHeaderManager;

  // Initialize when document is ready
  if (global.onDocumentReady) {
    global.onDocumentReady(function () {
      global.dynamicHeaderManager = new DynamicHeaderManager();
    });
  } else {
    // Fallback initialization
    document.addEventListener("DOMContentLoaded", function () {
      global.dynamicHeaderManager = new DynamicHeaderManager();
    });
  }

  // Reinitialize after AJAX content loads (for themes using AJAX)
  document.addEventListener("theme:ajax-loaded", function () {
    if (global.dynamicHeaderManager) {
      global.dynamicHeaderManager.destroy();
    }
    global.dynamicHeaderManager = new DynamicHeaderManager();
  });

  // Handle page visibility changes to pause/resume videos
  document.addEventListener("visibilitychange", function () {
    const videos = document.querySelectorAll(
      ".page-header-video-bg-ambient video, .hero__video-vertical video"
    );

    if (document.hidden) {
      // Page is hidden, pause videos
      videos.forEach(function (video) {
        if (!video.paused) {
          video.pause();
          video.setAttribute("data-was-playing", "true");
        }
      });
    } else {
      // Page is visible, resume videos that were playing
      videos.forEach(function (video) {
        if (
          video.getAttribute("data-was-playing") === "true" &&
          !(
            global.dynamicHeaderManager &&
            global.dynamicHeaderManager.prefersReducedMotion()
          )
        ) {
          video.play().catch(function (e) {
            console.log("Resume autoplay prevented:", e);
          });
          video.removeAttribute("data-was-playing");
        }
      });
    }
  });

  // jQuery compatibility (if jQuery is available)
  if (typeof jQuery !== "undefined") {
    jQuery(document).ready(function ($) {
      // Allow jQuery users to access the manager
      $.dynamicHeader = global.dynamicHeaderManager;

      // Trigger custom events
      $(document).on("dynamicheader:init", function () {
        if (global.dynamicHeaderManager) {
          global.dynamicHeaderManager.init();
        }
      });
    });
  }

  // CommonJS compatibility
  if (typeof module !== "undefined" && module.exports) {
    module.exports = DynamicHeaderManager;
  }
})(typeof window !== "undefined" ? window : this);
